import React from 'react';
import { Col, Row, Space, Tag, Typography } from 'antd';
import type { QuotationRevisionCompareResult } from '@/components/crm/QuotationRevisionComparePanel';
import { displayQuotationRevisionLabel } from '@/utils/quotationRevisionPolicy';
import { formatMoney, formatNumber, formatPercent, UAE_MONEY_PROFILE } from '@/utils/format';

const { Text } = Typography;

type Props = {
  comparison: QuotationRevisionCompareResult | undefined;
  loading?: boolean;
};

function deltaMoney(delta: number | null | undefined, currency: string): React.ReactNode {
  if (delta == null || Math.abs(delta) < 0.005) return <Text type="secondary">No change</Text>;
  const sign = delta < 0 ? '−' : '+';
  return (
    <Text type={delta < 0 ? 'danger' : 'success'}>
      {sign}
      {formatMoney(Math.abs(delta), { ...UAE_MONEY_PROFILE, currency })}
    </Text>
  );
}

export const QuotationRevisionImpactHero: React.FC<Props> = ({ comparison, loading }) => {
  if (loading) {
    return (
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">Loading revision impact…</Text>
      </div>
    );
  }
  if (!comparison?.commercial_impact) return null;

  const impact = comparison.commercial_impact;
  const currency = impact.currency || 'AED';
  const prevLabel = displayQuotationRevisionLabel(comparison.previous.revision_number);
  const curLabel = displayQuotationRevisionLabel(comparison.current.revision_number);

  const paymentDelta = comparison.field_deltas.find((f) =>
    String(f.field).toLowerCase().includes('payment')
  );
  const validityDelta = comparison.field_deltas.find((f) =>
    String(f.field).toLowerCase().includes('validity')
  );

  return (
    <div
      style={{
        marginBottom: 16,
        padding: 16,
        background: '#fafafa',
        borderRadius: 8,
        border: '1px solid #f0f0f0',
      }}
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Text strong style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
          Revision Impact
        </Text>
        <Tag color="blue">
          {prevLabel} → {curLabel}
        </Tag>
        <Tag color="purple">{comparison.classification}</Tag>
        <Tag color="orange">{comparison.reapproval_materiality}</Tag>
      </Space>
      <Row gutter={[24, 8]}>
        <Col xs={12} sm={8} md={4}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Sell
          </Text>
          <div>{deltaMoney(impact.selling_delta, currency)}</div>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Cost
          </Text>
          <div>{deltaMoney(impact.cost_delta, currency)}</div>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Margin
          </Text>
          <div>{deltaMoney(impact.margin_delta, currency)}</div>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Margin %
          </Text>
          <div>
            {impact.margin_pct_delta_pp == null || Math.abs(impact.margin_pct_delta_pp) < 0.005 ? (
              <Text type="secondary">No change</Text>
            ) : (
              <Text type={impact.margin_pct_delta_pp < 0 ? 'danger' : 'success'}>
                {impact.margin_pct_delta_pp < 0 ? '−' : '+'}
                {formatNumber(Math.abs(impact.margin_pct_delta_pp), { maximumFractionDigits: 2 })} pp
              </Text>
            )}
          </div>
        </Col>
        {validityDelta ? (
          <Col xs={12} sm={8} md={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Validity
            </Text>
            <div>
              {String(validityDelta.previous ?? '—')} → {String(validityDelta.current ?? '—')}
            </div>
          </Col>
        ) : null}
        {paymentDelta ? (
          <Col xs={12} sm={8} md={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Payment
            </Text>
            <div>
              {String(paymentDelta.previous ?? '—')} → {String(paymentDelta.current ?? '—')}
            </div>
          </Col>
        ) : null}
        <Col xs={12} sm={8} md={4}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Discount
          </Text>
          <div>
            {formatPercent(impact.previous_header_discount_pct, { semantics: 'whole', maximumFractionDigits: 1 })}
            {' → '}
            {formatPercent(impact.revised_header_discount_pct, { semantics: 'whole', maximumFractionDigits: 1 })}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default QuotationRevisionImpactHero;
